import { useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  consumeTableBackgroundNavigation,
  markIntentionalTableLeave,
  markTableBackgroundNavigation,
} from 'lib/api';
import { goBackInApp, navigateInApp } from 'lib/navigation';

interface UseTableExitNavigationOptions {
  fallbackPath: string;
  leaveTable: (battleUuid: string) => void;
  getBattleUuid: () => string | undefined;
  /** Path prefix for this table route, e.g. `/table/` or `/private/` */
  routePrefix: string;
}

export function useTableExitNavigation({
  fallbackPath,
  leaveTable,
  getBattleUuid,
  routePrefix,
}: UseTableExitNavigationOptions) {
  const navigate = useNavigate();
  const leavingRef = useRef(false);
  const leaveTableRef = useRef(leaveTable);
  leaveTableRef.current = leaveTable;
  const getBattleUuidRef = useRef(getBattleUuid);
  getBattleUuidRef.current = getBattleUuid;
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  const leaveSeat = useCallback(() => {
    if (leavingRef.current) return false;
    leavingRef.current = true;
    markIntentionalTableLeave();
    const uuid = getBattleUuidRef.current()?.trim();
    if (uuid) leaveTableRef.current(uuid);
    return true;
  }, []);

  const handleBack = useCallback(() => {
    if (!leaveSeat()) return;
    goBackInApp(navigateRef.current, fallbackPath);
  }, [leaveSeat, fallbackPath]);

  const handleLeave = handleBack;

  const handleOpenWallet = useCallback(() => {
    markTableBackgroundNavigation();
    navigateInApp(navigateRef.current, '/transactions');
  }, []);

  useEffect(() => {
    const onPopState = () => {
      if (consumeTableBackgroundNavigation()) {
        leavingRef.current = false;
        return;
      }
      if (leavingRef.current) return;
      if (!window.location.pathname.startsWith(routePrefix)) {
        leavingRef.current = true;
        markIntentionalTableLeave();
        const uuid = getBattleUuidRef.current()?.trim();
        if (uuid) leaveTableRef.current(uuid);
      }
    };

    window.addEventListener('popstate', onPopState);
    return () => {
      window.removeEventListener('popstate', onPopState);
      if (leavingRef.current) return;
      if (consumeTableBackgroundNavigation()) return;
      markIntentionalTableLeave();
      const uuid = getBattleUuidRef.current()?.trim();
      if (uuid) leaveTableRef.current(uuid);
    };
  }, [routePrefix]);

  return { handleBack, handleLeave, handleOpenWallet };
}
